import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AssetManager } from '../../../../../../../viewer/asset-manager';
import { DesignTool, DesignToolDialogState } from '../../../../../../../viewer/tools/design-tool';
import { saveTool } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/general';
import { useErrorHandling } from '../../../../../../hooks/use-error-handling';
import { Button } from '../../../../../button/button';
import { Dialog } from '../../../../../dialog/dialog';
import { InfoBox } from '../../../../../info-box/info-box';
import { Stack } from '../../../../../stack/stack';

type Props = {
  onConfirm: (wasSaved: boolean) => Promise<void>;
  onCancel: () => void;
  showAbortConfirmation: boolean;
  tool: DesignTool;
};

enum Status {
  Idle,
  Saving,
  Error,
  Aborting,
}

const DesignToolSaveDialog = (props: Props) => {
  const { t } = useTranslation();
  const { handleError, buildErrorList, includeErrorPaths } = useErrorHandling();
  const [status, setStatus] = React.useState(
    props.showAbortConfirmation ? Status.Idle : Status.Saving,
  );
  const loading = status === Status.Saving || status === Status.Aborting;
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { tool, onConfirm } = props;
  const isSavingRef = useRef(false);
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  const onSave = () => {
    saveTool(tool);

    if (tool.uiState === DesignToolDialogState.Saving) {
      setStatus(Status.Saving);
    }
  };

  React.useEffect(() => {
    if (status === Status.Saving) {
      if (isSavingRef.current) {
        return;
      }

      isSavingRef.current = true;

      const save = async () => {
        try {
          AssetManager.instance.addAsset(tool.asset);
          await tool.asset.saveGeometryToS3();
          await onConfirmRef.current(true);
        } catch (err) {
          handleError(err);
          setStatus(Status.Error);
        } finally {
          isSavingRef.current = false;
        }
      };

      void save();
    }
  }, [status, handleError, tool]);

  return (
    <Dialog
      closeButtonRef={closeButtonRef}
      closeIcon={false}
      closeOnDimmerClick={false}
      confirmButtonRef={confirmButtonRef}
      width={400}
      onClose={props.onCancel}
    >
      {{
        header: t('saveDialog.header', { ns: 'skyviewDesign' }),
        content: (
          <Content>
            {props.showAbortConfirmation && status !== Status.Error ? (
              <Stack direction="column" spacing={1}>
                <span>{t('saveDialog.saveChangesQuestion', { ns: 'skyviewDesign' })}</span>
                <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'exclamation'] }}>
                  {t('saveDialog.discardChangesWarning', { ns: 'skyviewDesign' })}
                </InfoBox>
              </Stack>
            ) : status === Status.Saving ? (
              <>
                <InfoBox color="yellow">
                  {t('saveDialog.savingChanges', { ns: 'skyviewDesign' })}
                </InfoBox>
              </>
            ) : null}

            {buildErrorList({
              filter: includeErrorPaths(['id', 'body.uploadId']),
              defaultMessage: t('saveDialog.errorSavingChanges', { ns: 'skyviewDesign' }),
              infoBoxProps: {
                topMargin: false,
              },
            })}
          </Content>
        ),
        footer: {
          left: props.showAbortConfirmation && (
            <Button
              color="error"
              disabled={loading}
              loading={loading}
              variant="contained"
              onClick={async () => {
                setStatus(Status.Aborting);
                await props.onConfirm(false);
              }}
            >
              {t('discard', { ns: 'common' })}
            </Button>
          ),
          right: (
            <>
              {props.showAbortConfirmation && (
                <Button
                  color="primary"
                  disabled={loading}
                  loading={loading}
                  ref={confirmButtonRef}
                  variant="contained"
                  onClick={onSave}
                >
                  {t('save', { ns: 'common' })}
                </Button>
              )}
              <Button
                disabled={loading}
                ref={closeButtonRef}
                variant="text"
                onClick={props.onCancel}
              >
                {status === Status.Error
                  ? t('close', { ns: 'common' })
                  : t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;

  section {
    display: flex;
    flex-direction: row;
    gap: 1em;
    justify-content: space-between;
    * {
      width: 100%;
    }
  }
`;

export { DesignToolSaveDialog };
